import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef
} from 'react';
import { useThemeMode } from 'flowbite-react';

const ThemeContext = createContext();
const DARK_MODE_KEY = 'dark-mode';
const FLOWBITE_MODE_KEY = 'flowbite-theme-mode';

const resolveInitialDarkMode = () => {
  const savedTheme = localStorage.getItem(DARK_MODE_KEY);
  if (savedTheme === 'true' || savedTheme === 'false') {
    return savedTheme === 'true';
  }

  const flowbiteTheme = localStorage.getItem(FLOWBITE_MODE_KEY);
  if (flowbiteTheme === 'dark' || flowbiteTheme === 'light') {
    return flowbiteTheme === 'dark';
  }

  // Final fallback: respect any server/injected html class.
  return document.documentElement.classList.contains('dark');
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(resolveInitialDarkMode);

  // flowbite-react's useThemeMode returns a fresh `setMode` reference on every
  // render. Holding it in a ref keeps our effect deps stable, which prevents
  // the effect from re-firing every render and oscillating with flowbite's
  // internal SYNC event listener.
  const { setMode } = useThemeMode();
  const setModeRef = useRef(setMode);
  setModeRef.current = setMode;

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => !prev);
  };

  useEffect(() => {
    localStorage.setItem(
      DARK_MODE_KEY,
      isDarkMode.toString()
    );

    const htmlElement = document.documentElement;
    const target = isDarkMode ? 'dark' : 'light';
    const hasDark = htmlElement.classList.contains('dark');

    if (isDarkMode && !hasDark) {
      htmlElement.classList.add('dark', 'sl-theme-dark');
    } else if (!isDarkMode && hasDark) {
      htmlElement.classList.remove('dark', 'sl-theme-dark');
    }

    // Only call flowbite setMode if its LS-backed value actually differs,
    // so we don't trigger its SYNC event on every effect run.
    if (localStorage.getItem(FLOWBITE_MODE_KEY) !== target) {
      setModeRef.current(target);
    }
  }, [isDarkMode]);

  const contextValue = React.useMemo(
    () => ({ isDarkMode, toggleDarkMode }),
    [isDarkMode]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
